import { v4 as uuidv4 } from 'uuid';
import { CookiesService } from '@packages/shared/core/common/services';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { Logger } from '@packages/shared/core/common/logger/interfaces';
import { YEAR_IN_DAYS } from '@packages/shared/core/common/constants/time';
import { Config } from '@packages/shared/interfaces';
import { FeatureRawResult, FeatureValue } from '../entities';
import { FeatureFlags } from './FeatureFlags';
import { FeatureFlagsDecorator } from './FeatureFlagsDecorator';

export class CookiesIdFeatureFlagsDecorator extends FeatureFlagsDecorator {
  readonly storageKey = 'split_id';

  private cookies: CookiesService;

  private logger: Logger;

  private config: Config;

  private expires = YEAR_IN_DAYS;

  private currentIdentifier = '';

  constructor(featureFlags: FeatureFlags, cookies: CookiesService, config: Config, logger: Logger) {
    super(featureFlags);
    this.cookies = cookies;
    this.logger = logger;
    this.config = config;
  }

  getExperimentGroup<T extends FeatureValue>(experimentName: string): T | null {
    this.maybeUpdateFeatureFlagsId();
    return super.getExperimentGroup(experimentName);
  }

  getFeatureValue<T extends FeatureValue = FeatureValue>(featureName: string, defaultValue?: T): T | null {
    this.maybeUpdateFeatureFlagsId();
    return super.getFeatureValue(featureName, defaultValue);
  }

  getFeatureRawResult<T extends FeatureValue>(featureName: string): FeatureRawResult<T | null> {
    this.maybeUpdateFeatureFlagsId();
    return super.getFeatureRawResult(featureName);
  }

  isFeatureOn(featureName: string): boolean {
    this.maybeUpdateFeatureFlagsId();
    return super.isFeatureOn(featureName);
  }

  isFeatureOff(featureName: string): boolean {
    this.maybeUpdateFeatureFlagsId();
    return super.isFeatureOff(featureName);
  }

  getSplitId(): string {
    this.maybeUpdateFeatureFlagsId();
    return super.getSplitId();
  }

  private maybeUpdateFeatureFlagsId() {
    const newIdentifier = this.getIdentifier();

    if (newIdentifier === this.currentIdentifier) return;

    this.currentIdentifier = newIdentifier;
    super.setIdentifier(newIdentifier);
  }

  private getIdentifier() {
    const identifier = this.getIdentifierFromCookies();

    if (identifier) {
      return identifier;
    }

    return this.createIdentifier();
  }

  private createIdentifier() {
    const identifier = uuidv4();

    try {
      this.cookies.set(this.storageKey, identifier, {
        expires: this.expires,
        domain: this.config.domainRoot,
      });
      return identifier;
    } catch (e) {
      const err = normalizeError(e);

      this.logger.error('Failed to save identifier', {
        data: {
          errorMessage: err.message,
        },
      });

      return identifier;
    }
  }

  private getIdentifierFromCookies(): string {
    try {
      return this.cookies.get(this.storageKey) || '';
    } catch (e) {
      const err = normalizeError(e);

      this.logger.error('Failed to load identifier from storage', {
        data: {
          errorMessage: err.message,
        },
      });
      return '';
    }
  }
}
